import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { CircleDot } from "lucide-react";
import { toast } from "sonner";

export default function Plinko() {
  const { user, currency, refresh } = useApp();
  const [searchParams] = useSearchParams();
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [rows, setRows] = useState(() => {
    const p = Number(searchParams.get("rows"));
    return [8, 12, 16].includes(p) ? p : 12;
  });
  const [risk, setRisk] = useState(() => {
    const p = searchParams.get("risk");
    return ["low","medium","high"].includes(p) ? p : "medium";
  });
  const [config, setConfig] = useState(null);
  const [result, setResult] = useState(null);
  const [ballPos, setBallPos] = useState(null); // {row, col}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/instant/plinko/config").then(r=>setConfig(r.data.multipliers)).catch(()=>{});
  }, []);

  const drop = async () => {
    setBusy(true);
    setBallPos(null);
    try {
      const r = await api.post("/instant/plinko/drop", {
        bet: Number(bet), currency, rows: Number(rows), risk,
      });
      // Animate: walk down path
      let col = 0;
      setBallPos({ row: 0, col: 0 });
      for (let i = 0; i < r.data.path.length; i++) {
        await new Promise(res => setTimeout(res, 90));
        col += r.data.path[i];
        setBallPos({ row: i + 1, col });
      }
      setResult(r.data);
      refresh();
      if (r.data.payout > r.data.multiplier * 0 + Number(bet)) toast.success(`${r.data.multiplier}x → ${fmt(r.data.payout, currency)}`);
      else if (r.data.payout > 0) toast(`${r.data.multiplier}x → ${fmt(r.data.payout, currency)}`);
      else toast.error("Lost");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const mults = config?.[risk]?.[rows] || [];

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <CircleDot className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Plinko</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">Drop a ball · land in a slot</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="lux-card p-6">
          <div className="relative mx-auto" style={{ maxWidth: 640 }}>
            {/* pegs */}
            <div className="flex flex-col items-center gap-2 pt-2">
              {Array.from({ length: rows }).map((_, r) => (
                <div key={r} className="flex gap-2">
                  {Array.from({ length: r + 1 }).map((_, c) => {
                    const active = ballPos && ballPos.row === r + 1 && ballPos.col === c;
                    return (
                      <div key={c} className="relative">
                        <div className="w-2 h-2 rounded-full bg-white/40"/>
                        {active && <div className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-[#d4af37] animate-pulse shadow-lg shadow-[#d4af37]/60"/>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {/* multipliers */}
            <div className="flex gap-1 justify-center mt-3">
              {mults.map((m, i) => {
                const highlight = result && result.slot === i;
                return (
                  <div key={i} data-testid={`plinko-slot-${i}`}
                       className={`px-1.5 py-1 rounded text-[9px] font-bold text-center min-w-[28px] transition ${
                         highlight ? "bg-[#d4af37] text-black scale-110" :
                         m >= 5 ? "bg-red-900/50 text-red-300" :
                         m >= 1 ? "bg-emerald-900/40 text-emerald-300" :
                         "bg-white/10 text-white/60"
                       }`}>{m}x</div>
                );
              })}
            </div>
          </div>
          {result && (
            <div className="mt-6 text-center">
              <div className={result.payout > Number(bet) ? "text-emerald-400" : "text-white/60"}>
                Landed at {result.multiplier}x · {result.payout > 0 ? `+${fmt(result.payout, currency)}` : "Lost"}
              </div>
            </div>
          )}
        </div>

        <div className="lux-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Bet</label>
            <input data-testid="plinko-bet" type="number" value={bet} onChange={e=>setBet(e.target.value)}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 font-serif text-xl focus:border-[#d4af37]"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Risk</label>
            <div className="grid grid-cols-3 gap-2">
              {["low","medium","high"].map(r=>(
                <button key={r} data-testid={`plinko-risk-${r}`}
                        onClick={()=>setRisk(r)}
                        className={`px-3 py-2 rounded text-xs uppercase ${risk===r?"btn-gold":"btn-ghost"}`}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Rows: {rows}</label>
            <div className="grid grid-cols-3 gap-2">
              {[8, 12, 16].map(r=>(
                <button key={r} data-testid={`plinko-rows-${r}`}
                        onClick={()=>setRows(r)}
                        className={`px-3 py-2 rounded text-xs ${rows===r?"btn-gold":"btn-ghost"}`}>{r}</button>
              ))}
            </div>
          </div>
          <div className="rounded bg-black/40 border border-white/10 p-3 text-xs text-white/50">
            Balance: <span className="text-[#d4af37] font-bold">{fmt(currency==="HUF"?user?.balance_huf:user?.balance_eur, currency)}</span>
          </div>
          <button data-testid="plinko-drop" onClick={drop} disabled={busy}
                  className="w-full btn-burgundy px-6 py-4 rounded-md text-lg uppercase tracking-widest">
            ● Drop Ball
          </button>
        </div>
      </div>
    </div>
  );
}
